import { useEffect, useRef, useCallback } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_ENDPOINTS } from 'config/ApiConfig';
import useAuthStore from 'Store/useAuthStore';
import useHrisUploadStore from 'Store/useHrisUploadStore';
import useNotificationStoreV2 from 'Store/useNotificationStoreV2';
import { SbNotification } from 'uiCore';
import { isTokenValid } from 'Utils/tokenValidation';

/**
 * HRIS Upload SSE hook — real-time progress for the employee mapping job.
 *
 * Flow:
 *  1. After upload API returns, the component calls useHrisUploadStore.setUpload(uploadId).
 *  2. This hook detects isActive=true and opens an SSE stream to HRIS_STREAM/{uploadId}.
 *  3. Each SSE event calls updateProgress() — feeds the progress bar in the UI.
 *  4. On terminal status ('Task Completed' / 'HRIS Mapping Failed'):
 *       - setTerminal() marks the store done (progress bar → 100%, isActive → false)
 *       - A bell notification is pushed so the user can navigate back if they left the page
 *       - Stream is closed
 *  5. key_does_not_exist: Redis stream not yet written by Cloud Run (5-30s startup gap).
 *     Retried up to MAX_SSE_RETRIES times before showing a warning.
 *  6. Connection error: same retry logic as above.
 *  7. No polling fallback — SSE is the sole mechanism (backend confirmed reliable).
 */

const TERMINAL_STATUSES = new Set(['Task Completed', 'HRIS Mapping Failed']);

const MAX_SSE_RETRIES = 3;
const SSE_RETRY_DELAY_MS = 2000;

const useHrisUploadSSE = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const uploadId = useHrisUploadStore((s) => s.uploadId);
  const isActive = useHrisUploadStore((s) => s.isActive);

  const eventSourceRef = useRef(null);
  const cancelledRef = useRef(false);
  const retryCountRef = useRef(0);

  const connectSSE = useCallback(() => {
    if (cancelledRef.current || !uploadId || !accessToken) return;
    if (!isTokenValid(accessToken, 'useHrisUploadSSE')) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const es = new EventSourcePolyfill(`${API_ENDPOINTS.HRIS_STREAM}${uploadId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      heartbeatTimeout: 60 * 60 * 1000, // 1 hour — mapping job can take 15+ min
    });

    eventSourceRef.current = es;

    es.onmessage = (event) => {
      if (cancelledRef.current) return;
      try {
        let data = JSON.parse(event.data);

        // Unwrap double-wrapped format: {"data": "{\"status\":...}"}
        if (data.data && typeof data.data === 'string' && !data.status) {
          try { data = JSON.parse(data.data); } catch (_) { /* use as-is */ }
        }

        // Redis stream not yet created — Cloud Run Job hasn't started writing yet
        if (data.event === 'key_does_not_exist') {
          es.close();
          eventSourceRef.current = null;
          if (retryCountRef.current < MAX_SSE_RETRIES) {
            retryCountRef.current += 1;
            setTimeout(connectSSE, SSE_RETRY_DELAY_MS);
          } else {
            SbNotification.warning(
              'Progress Unavailable',
              'Unable to connect to the progress stream. Please refresh the page to check status.',
              { key: 'hris-sse-retry-exhausted' }
            );
          }
          return;
        }

        retryCountRef.current = 0;
        useHrisUploadStore.getState().updateProgress(data);

        if (TERMINAL_STATUSES.has(data.status)) {
          const mapped = Number(data.mapped_count) || 0;
          const needsAction = Number(data.needs_action_count) || 0;
          const flagged = Number(data.flagged_count) || 0;

          const finalStatus = data.status === 'Task Completed'
            ? (needsAction > 0 ? 'NEEDS_ACTION' : 'COMPLETE')
            : 'FAILED';

          useHrisUploadStore.getState().setTerminal(finalStatus, data);

          const isSuccess = data.status === 'Task Completed';
          const bellMessage = isSuccess
            ? `HRIS mapping completed — ${mapped} mapped${needsAction > 0 ? `, ${needsAction} need action` : ''}${flagged > 0 ? `, ${flagged} flagged` : ''}`
            : `HRIS mapping failed${data.message ? `: ${data.message}` : ''}`;

          // Stable id (no Date.now) so the component can remove it after confirm mapping
          useNotificationStoreV2.getState().addNotification({
            id: `hris-mapping-${uploadId}`,
            message: bellMessage,
            message_type: isSuccess ? 'success' : 'error',
            entity_id: uploadId,
            context_url: `/admin-setup/employee-data?upload_id=${uploadId}`,
            created_at: Math.floor(Date.now() / 1000),
          });

          es.close();
          eventSourceRef.current = null;
        }
      } catch (err) {
        console.error('[HrisSSE] parse error:', err);
      }
    };

    es.onerror = () => {
      if (cancelledRef.current) return;
      es.close();
      eventSourceRef.current = null;
      if (retryCountRef.current < MAX_SSE_RETRIES) {
        retryCountRef.current += 1;
        setTimeout(connectSSE, SSE_RETRY_DELAY_MS);
      } else {
        SbNotification.warning(
          'Connection Lost',
          'Lost connection to the progress stream. Please refresh the page to check status.',
          { key: 'hris-sse-connection-lost' }
        );
      }
    };

    es.addEventListener('end', () => {
      es.close();
      eventSourceRef.current = null;
    });
  }, [uploadId, accessToken]);

  useEffect(() => {
    if (!uploadId || !isActive || !accessToken) return;

    cancelledRef.current = false;
    retryCountRef.current = 0;
    connectSSE();

    return () => {
      cancelledRef.current = true;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [uploadId, isActive, accessToken, connectSSE]);

  return useHrisUploadStore();
};

export default useHrisUploadSSE;
